/**
 * Universal content extractor for all file types
 * Routes binary/complex files to Lovable's document parser
 * Handles simple text files client-side
 */

import { supabase } from "@/integrations/supabase/client";

export async function extractContentFromFile(file: File): Promise<{
  content: string;
  method: 'parser' | 'client';
  pages?: number;
}> {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  
  // Define simple text files that can be read directly on client
  const isSimpleText = 
    fileType.includes('text/plain') || 
    fileName.endsWith('.txt') || 
    fileName.endsWith('.md') ||
    fileName.endsWith('.csv');
  
  if (isSimpleText && file.size < 500000) { // 500KB limit for client-side reading
    try {
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string || '');
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });
      return { content, method: 'client' };
    } catch (error) {
      console.error('Client-side text reading failed, using parser:', error);
      // Fall through to parser
    }
  }
  
  // All other files use Lovable's document parser via edge function
  // This handles: PDF, DOCX, DOC, PPTX, PPT, XLSX, XLS, MP3, WAV, M4A, images, etc.
  return await parseDocumentViaAPI(file);
}

async function parseDocumentViaAPI(file: File): Promise<{
  content: string;
  method: 'parser';
  pages?: number;
}> {
  // Upload file temporarily to storage for parsing
  const tempPath = `temp/${Date.now()}-${file.name}`;
  
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('knowledge-bases')
    .upload(tempPath, file);
  
  if (uploadError) {
    throw new Error(`Failed to upload file for parsing: ${uploadError.message}`);
  }
  
  try {
    // Get public URL for the edge function to access
    const { data: { publicUrl } } = supabase.storage
      .from('knowledge-bases')
      .getPublicUrl(tempPath);
    
    // Call edge function to parse document
    const { data, error } = await supabase.functions.invoke('parse-document', {
      body: { 
        fileUrl: publicUrl,
        fileName: file.name,
        fileType: file.type
      }
    });
    
    if (error) {
      throw new Error(`Document parsing failed: ${error.message}`);
    }
    
    return {
      content: data.text || '',
      method: 'parser',
      pages: data.pages
    };
  } finally {
    // Clean up temp file
    await supabase.storage
      .from('knowledge-bases')
      .remove([tempPath]);
  }
}
